<template>
  <div class="flex flex-col gap-5">
    <template v-if="calculation.status === 'calculated' && result?.difference">
      <!-- A grid, because the three text parts sit differently per screen: on a phone the amount
           stands next to the icon with the two lines below it across the full width, from sm up the
           icon covers both rows with the amount and its label on one line beside it. -->
      <div
        class="result-item grid w-full grid-cols-[auto_1fr] items-center gap-x-2 rounded-xl p-5 sm:grid-cols-[auto_auto_1fr] sm:gap-x-6 sm:p-6"
        :class="calculation.paysLess ? 'bg-green' : 'bg-orange'"
        :style="{ animationDelay: '120ms' }"
      >
        <div
          class="flex size-[46px] shrink-0 items-center justify-center self-center rounded-[9px] bg-white sm:row-span-2 sm:size-[76px]"
        >
          <CheckIcon v-if="calculation.paysLess" class="w-[26px] text-green sm:w-[42px]" aria-hidden="true" />
          <AlertTriangleIcon v-else class="w-[26px] text-orange sm:w-[42px]" aria-hidden="true" />
        </div>
        <p class="text-[28px] leading-none font-bold text-white sm:self-baseline sm:text-[44px]">
          {{ formatEuro(result.difference.monthly) }}
        </p>
        <p class="col-span-2 text-lg font-light sm:col-span-1 sm:self-baseline" :class="supportingText">
          {{ calculation.paysLess ? 'per maand' : 'per maand extra' }}
        </p>
        <p
          class="col-span-2 mt-1 text-sm leading-[22px] font-light sm:mt-2 sm:text-lg sm:leading-7"
          :class="supportingText"
        >
          Dat is ongeveer
          <span class="font-bold text-white sm:text-[22px]">{{ formatEuro(result.difference.annual) }} per jaar</span>
        </p>
      </div>

      <!-- Nothing to solve for a visitor who gains from the change, so the action plan and its
           invitation to leave an address stay away (client decision on #6331). -->
      <div
        v-if="!calculation.paysLess"
        ref="plan"
        class="result-item flex items-center gap-6"
        :style="{ animationDelay: '240ms' }"
      >
        <div class="relative min-w-0 flex-1">
          <ActionPlanCard />
          <!-- The speech tail: towards the expert photo from sm up (14x26), on top below that
               where the photo is gone (27x14). Poking 10px out of the card, 4px overlap against
               hairline gaps. -->
          <div
            class="pointer-events-none absolute -top-[10px] left-1/2 h-[14px] w-[27px] -translate-x-1/2 [clip-path:polygon(50%_0,0_100%,100%_100%)] bg-white sm:hidden"
            aria-hidden="true"
          />
          <div
            class="pointer-events-none absolute top-1/2 -right-[10px] hidden h-[26px] w-[14px] -translate-y-1/2 [clip-path:polygon(0_0,100%_50%,0_100%)] bg-white sm:block"
            aria-hidden="true"
          />
        </div>
        <div class="relative hidden shrink-0 sm:block" aria-hidden="true">
          <img
            :src="sunRays"
            alt=""
            class="absolute -top-7 -right-4 w-[64px] origin-[15%_87%] motion-safe:animate-sun-rays"
          />
          <img :src="michelle" alt="" class="relative size-[85px] rounded-full object-cover ring-2 ring-white" />
        </div>
      </div>
    </template>

    <!-- Stand-in in panel style: there is no design for the missing-tariffs state yet (#6313). -->
    <div v-else class="result-item flex flex-col gap-2 rounded-xl bg-white p-6" :style="{ animationDelay: '120ms' }">
      <h4 class="text-[20px] font-bold text-heading">We kunnen dit nu niet berekenen</h4>
      <p class="text-base leading-5 font-light text-body">
        {{ result?.message ?? 'Voor jouw situatie zijn er op dit moment geen geldige tarieven beschikbaar.' }}
      </p>
    </div>

    <button
      type="button"
      class="result-item mx-auto -my-[11px] cursor-pointer rounded-sm py-[11px] text-[14px] leading-[22px] font-medium text-white underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      :style="{ animationDelay: '360ms' }"
      @click="emit('restart')"
    >
      Opnieuw berekenen
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, useTemplateRef } from 'vue'

import michelle from '@/assets/images/michelle.png'
import AlertTriangleIcon from '@/assets/svg/alert-triangle.svg?component'
import CheckIcon from '@/assets/svg/check.svg?component'
import sunRays from '@/assets/svg/sun-rays.svg'
import ActionPlanCard from '@/components/expert-panel/ActionPlanCard.vue'
import { prefersReducedMotion } from '@/stores/animation'
import { useCalculationStore } from '@/stores/calculation'
import { formatEuro } from '@/utils/currency'
import { isPhoneLayout } from '@/utils/viewport'

const emit = defineEmits<{ restart: [] }>()

const calculation = useCalculationStore()

const result = computed(() => calculation.result)

const supportingText = computed(() => (calculation.paysLess ? 'text-green-pale' : 'text-orange-pale'))

const plan = useTemplateRef<HTMLElement>('plan')

/**
 * On a phone the action plan falls below the fold, and its green button is the point of the whole
 * result (#6331). Bring it into view the moment the result appears, so the scroll rides along with
 * it rising in instead of following as a second movement. Aligned on its bottom, which keeps the
 * amount above it readable. Without an action plan there is nothing to bring into view.
 */
onMounted(async () => {
  if (!isPhoneLayout()) {
    return
  }

  await nextTick()

  plan.value?.scrollIntoView?.({ block: 'end', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
})
</script>
